import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithTokenHandling } from '../baseQuery';
import { buildSearchParamsAuto } from '../utils';
import { providersApi } from '../providers/providersApi';
import { discoveryApi } from '../discovery/discoveryApi';
import { strategyApi } from '../strategy/strategyApi';
import {
  ALL_STRATEGY_MEMBER_TAGS,
} from '../strategy/constants';
import type { RootState } from 'src/redux/reducers';
import type { DiscoveryListRequest } from '../discovery/types';
import type {
  OneTimeSyncTracker,
  MappingAttribute,
  CRMOperationFailure,
} from './types';

const INTEGRATION_MAPPINGS = 'INTEGRATION_MAPPINGS';
const INTEGRATION_SETTINGS = 'INTEGRATION_SETTINGS';
const CRM_OBJECTS = 'CRM_OBJECTS';
const CRM_SYNC_STATUS = 'CRM_SYNC_STATUS';
const INTEGRATION_TOKEN = 'INTEGRATION_TOKEN';
const AUTOPOPULATE_OPTIONS = 'AUTOPOPULATE_OPTIONS';
const USER_INPUT_OPTIONS = 'USER_INPUT_OPTIONS';
const AVAILABLE_INTEGRATIONS = 'AVAILABLE_INTEGRATIONS';
const CRM_OPERATION_FAILURES = 'CRM_OPERATION_FAILURES';

interface GetMappingsParams extends MedScout.Pagination, MedScout.Sortable {
  crm?: string;
}

interface GetMappingsResponse extends MedScout.PaginatedResponse {
  results: MedScout.Mapping[];
}

interface GetMappingByIdParams {
  id: string;
}

interface GetSearchCRMParams {
  crmType: string;
  q: string;
  option?: string | number;
  crm: string;
}

interface GetObjectTypeAttributesParams {
  objectType: string;
}

interface GetCrmObjectsParams {
  crm: string;
  crmType: string;
  writable_only?: boolean;
}

interface GetSettingsParams {
  page?: number;
  pageSize?: number;
  crm?: string;
}

interface GetAutopopulateOptionsParams {
  mappingId?: string | number;
  [key: string]: any;
}

interface GetUserInputOptionsParams {
  mappingId?: string | number;
  [key: string]: any;
}

interface GetCRMSyncHistoryParams {
  mappingId?: string | number;
  page?: number;
  pageSize?: number;
  type?: 'push' | 'pull';
  [key: string]: any;
}

interface GetCRMOperationFailuresParams {
  mappingId?: string | number;
  /** 1-based, unlike getCRMSyncHistory's 0-based page. */
  page?: number;
  pageSize?: number;
  sort?: string;
  order?: string;
}

// Note: object_id should be passed in the data, not in params
interface PushToCRMParams extends Omit<MedScout.CRMObject, 'object_id'> {
  object_id?: string | number;
}

// Original useLinkToCRM accepted MedScout.CRMObject directly
// Components may pass additional fields like 'crm'
// Make fields optional since not all usages provide it
interface LinkToCRMParams extends Omit<MedScout.CRMObject, 'fields'> {
  crm?: string;
  fields?: Record<string, any>;
  [key: string]: any;
}

// Original useTestPushToCRM accepted { data, isDryRun }
interface TestPushToCRMParams {
  data: Omit<MedScout.CRMObject, 'object_id' | 'object_type'>;
  isDryRun: boolean;
}

// Original useStartOneTimeSync accepted { mappingId, direction }
interface StartOneTimeSyncParams {
  mappingId: number | string;
  direction: string;
}

// Original useInitiateIntegration accepted { crm, username }
interface InitiateIntegrationParams {
  crm: string;
  username: string;
}

interface AddMappingOptionParams {
  mappingId: string | number;
  data: any;
}

interface UpdateAutopopulateOptionParams {
  mappingId: string | number;
  data: any;
}

interface DeleteAutopopulateOptionParams {
  mappingId: string | number;
  data: any;
}

interface UpdateUserInputOptionParams {
  mappingId: string | number;
  data: any;
}

interface DeleteUserInputOptionParams {
  mappingId: string | number;
  data: any;
}

interface DiscoveryResultCRMLink {
  id?: number;
  crm?: string;
  crm_status?: string | null;
}

interface DiscoveryResultItem {
  provider_id: string | number;
  crm_links?: DiscoveryResultCRMLink[];
}

export const integrationsApi = createApi({
  reducerPath: 'integrationsApi',
  baseQuery: baseQueryWithTokenHandling,
  tagTypes: [
    INTEGRATION_MAPPINGS,
    INTEGRATION_SETTINGS,
    CRM_OBJECTS,
    CRM_SYNC_STATUS,
    INTEGRATION_TOKEN,
    AUTOPOPULATE_OPTIONS,
    USER_INPUT_OPTIONS,
    AVAILABLE_INTEGRATIONS,
    CRM_OPERATION_FAILURES,
  ],
  endpoints: (builder) => ({
    getMappings: builder.query<GetMappingsResponse, GetMappingsParams>({
      query: ({ sort, order, page = 0, pageSize, crm }) => {
        const queryParams = buildSearchParamsAuto({
          sort,
          order,
          page: page + 1,
          pageSize,
          crm,
        });
        return {
          url: queryParams ? `v1/integrations/mappings/?${queryParams}` : 'v1/integrations/mappings/',
          method: 'GET',
        };
      },
      providesTags: [{ type: INTEGRATION_MAPPINGS, id: 'LIST' }],
    }),

    getMappingById: builder.query<MedScout.Mapping, GetMappingByIdParams>({
      query: ({ id }) => ({
        url: `v1/integrations/mappings/${id}/`,
        method: 'GET',
      }),
      providesTags: (result, error, { id }) => [
        { type: INTEGRATION_MAPPINGS, id },
      ],
    }),

    searchCRM: builder.query<MedScout.CRMOptionLookup[], GetSearchCRMParams>({
      query: ({ crmType, q, option, crm }) => {
        const queryParams = buildSearchParamsAuto({
          q,
          option,
          crm,
        });
        return {
          url: queryParams ? `v1/integrations/crm-object/${crmType.toLowerCase()}/search/?${queryParams}` : `v1/integrations/crm-object/${crmType.toLowerCase()}/search/`,
          method: 'GET',
        };
      },
      providesTags: [{ type: CRM_OBJECTS, id: 'SEARCH' }],
    }),

    getObjectTypeAttributes: builder.query<MappingAttribute[], GetObjectTypeAttributesParams>({
      query: ({ objectType }) => ({
        url: `v1/integrations/mappings/${objectType}/available-attributes/`,
        method: 'GET',
      }),
      providesTags: [{ type: CRM_OBJECTS, id: 'ATTRIBUTES' }],
    }),

    getCrmObjects: builder.query<MedScout.CRMApiObject[], GetCrmObjectsParams>({
      query: ({ crmType, crm, writable_only = true }) => {
        const queryParams = buildSearchParamsAuto({ crm, writable_only });
        return {
          url: `v1/integrations/crm-object/${crmType.toLowerCase()}/destination-fields/?${queryParams}`,
          method: 'GET',
        };
      },
      providesTags: [{ type: CRM_OBJECTS, id: 'LIST' }],
    }),

    getSettings: builder.query<MedScout.UseGetSettingsResponse, GetSettingsParams>({
      query: (params = {}) => {
        const queryParams = buildSearchParamsAuto(params);
        return {
          url: queryParams ? `v1/integrations/settings/?${queryParams}` : 'v1/integrations/settings/',
          method: 'GET',
        };
      },
      providesTags: [{ type: INTEGRATION_SETTINGS, id: 'LIST' }],
    }),

    getAutopopulateOptions: builder.query<MedScout.PaginatedResponse & { results: MedScout.AutopopulateOption[] }, GetAutopopulateOptionsParams>({
      query: ({ mappingId, page, pageSize, ...params }) => {
        const queryParams = buildSearchParamsAuto({
          ...params,
          page: page !== undefined ? page + 1 : undefined,
          pageSize,
        });
        return {
          url: queryParams ? `v1/integrations/mappings/${mappingId}/options/?${queryParams}` : `v1/integrations/mappings/${mappingId}/options/`,
          method: 'GET',
        };
      },
      providesTags: [{ type: AUTOPOPULATE_OPTIONS, id: 'LIST' }],
    }),

    getUserInputOptions: builder.query<MedScout.PaginatedResponse & { results: MedScout.UserInputOption[] }, GetUserInputOptionsParams>({
      query: ({ mappingId, page, pageSize, ...params }) => {
        const queryParams = buildSearchParamsAuto({
          ...params,
          page: page !== undefined ? page + 1 : undefined,
          pageSize,
        });
        return {
          url: queryParams ? `v1/integrations/mappings/${mappingId}/user-inputs/?${queryParams}` : `v1/integrations/mappings/${mappingId}/user-inputs/`,
          method: 'GET',
        };
      },
      providesTags: [{ type: USER_INPUT_OPTIONS, id: 'LIST' }],
    }),

    getAvailableIntegrations: builder.query<MedScout.Integration[], void>({
      query: () => ({
        url: 'v1/integrations/available-integrations/',
        method: 'GET',
      }),
      providesTags: [{ type: AVAILABLE_INTEGRATIONS, id: 'LIST' }],
    }),

    getIntegrationToken: builder.query<{ token: string }, { username?: string } | void>({
      query: (params) => {
        const queryParams =
          params && 'username' in params && params.username
            ? buildSearchParamsAuto({ username: params.username })
            : '';
        return {
          url: queryParams ? `v1/integrations/integrations-token/?${queryParams}` : 'v1/integrations/integrations-token/',
          method: 'GET',
        };
      },
      providesTags: [{ type: INTEGRATION_TOKEN, id: 'TOKEN' }],
    }),

    getAllStatuses: builder.query<{ crm_type: string; crm_status: string }[], void>({
      query: () => ({
        url: 'v1/integrations/crm-links/distinct-statuses/',
        method: 'GET',
      }),
      providesTags: [{ type: CRM_SYNC_STATUS, id: 'LIST' }],
    }),

    getCRMSyncHistory: builder.query<MedScout.PaginatedResponse & { results: OneTimeSyncTracker[] }, GetCRMSyncHistoryParams>({
      query: ({ mappingId, page, pageSize, ...params }) => {
        const queryParams = buildSearchParamsAuto({
          ...params,
          page: page !== undefined ? page + 1 : undefined,
          pageSize,
        });
        return {
          url: queryParams ? `v1/integrations/mappings/${mappingId}/one-time-sync-trackers/?${queryParams}` : `v1/integrations/mappings/${mappingId}/one-time-sync-trackers/`,
          method: 'GET',
        };
      },
      providesTags: [{ type: CRM_SYNC_STATUS, id: 'HISTORY' }],
    }),

    getCRMOperationFailures: builder.query<MedScout.PaginatedResponse & { results: CRMOperationFailure[] }, GetCRMOperationFailuresParams>({
      query: ({ mappingId, page, pageSize, sort, order }) => {
        // Sending one without the other is a 400; the API defaults to newest-first.
        const ordering = sort && order ? { sort, order } : {};
        const queryParams = buildSearchParamsAuto({
          page,
          pageSize,
          ...ordering,
        });
        return {
          url: `v1/integrations/mappings/${mappingId}/failures/?${queryParams}`,
          method: 'GET',
        };
      },
      providesTags: [{ type: CRM_OPERATION_FAILURES, id: 'LIST' }],
    }),

    addMapping: builder.mutation<MedScout.Mapping, MedScout.Mapping>({
      query: (data) => ({
        url: 'v1/integrations/mappings/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: INTEGRATION_MAPPINGS, id: 'LIST' }],
    }),

    updateMapping: builder.mutation<MedScout.Mapping, MedScout.Mapping>({
      query: (data) => ({
        url: `v1/integrations/mappings/${data.id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, data) => [
        { type: INTEGRATION_MAPPINGS, id: 'LIST' },
        { type: INTEGRATION_MAPPINGS, id: data.id },
      ],
    }),

    deleteMapping: builder.mutation<void, string>({
      query: (id) => ({
        url: `v1/integrations/mappings/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: INTEGRATION_MAPPINGS, id: 'LIST' }],
    }),

    addMappingOption: builder.mutation<void, AddMappingOptionParams>({
      query: ({ mappingId, data }) => ({
        url: `v1/integrations/mappings/${mappingId}/`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [
        { type: AUTOPOPULATE_OPTIONS, id: 'LIST' },
        { type: USER_INPUT_OPTIONS, id: 'LIST' },
      ],
    }),

    updateAutopopulateOption: builder.mutation<
      MedScout.AutopopulateOption,
      UpdateAutopopulateOptionParams
    >({
      query: ({ mappingId, data }) => ({
        url: `v1/integrations/mappings/${mappingId}/options/${data.id}/`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: [{ type: AUTOPOPULATE_OPTIONS, id: 'LIST' }],
    }),

    deleteAutopopulateOption: builder.mutation<
      void,
      DeleteAutopopulateOptionParams
    >({
      query: ({ mappingId, data }) => ({
        url: `v1/integrations/mappings/${mappingId}/options/${data.id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: AUTOPOPULATE_OPTIONS, id: 'LIST' }],
    }),

    updateUserInputOption: builder.mutation<MedScout.UserInputOption, UpdateUserInputOptionParams>({
      query: ({ mappingId, data }) => ({
        url: `v1/integrations/mappings/${mappingId}/user-inputs/${data.id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: [{ type: USER_INPUT_OPTIONS, id: 'LIST' }],
    }),

    deleteUserInputOption: builder.mutation<
      void,
      DeleteUserInputOptionParams
    >({
      query: ({ mappingId, data }) => ({
        url: `v1/integrations/mappings/${mappingId}/user-inputs/${data.id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: USER_INPUT_OPTIONS, id: 'LIST' }],
    }),

    addSetting: builder.mutation<MedScout.Setting, Partial<MedScout.Setting>>({
      query: (data) => ({
        url: 'v1/integrations/settings/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: INTEGRATION_SETTINGS, id: 'LIST' }],
    }),

    updateSetting: builder.mutation<MedScout.Setting, Partial<MedScout.Setting> & { id: number }>({
      query: (data) => ({
        url: `v1/integrations/settings/${data.id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: [{ type: INTEGRATION_SETTINGS, id: 'LIST' }],
    }),

    deleteSetting: builder.mutation<void, { id: number }>({
      query: (data) => ({
        url: `v1/integrations/settings/${data?.id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: INTEGRATION_SETTINGS, id: 'LIST' }],
    }),

    pushToCRM: builder.mutation<Record<string, any>, PushToCRMParams>({
      query: (data) => ({
        url: 'v1/integrations/push-to-crm/',
        method: 'POST',
        body: data,
      }),
      async onQueryStarted(arg, { queryFulfilled, dispatch, getState }) {
        try {
          const { data: result } = await queryFulfilled;

          // Patch the affected provider's crm_links directly in all active discovery
          // list cache entries instead of invalidating the whole list — avoids a full
          // reload of all results when only one row changed.
          const state = getState() as RootState;
          const discoveryQueries = state.discoveryApi.queries;
          for (const entry of Object.values(discoveryQueries)) {
            if (entry?.endpointName !== 'getDiscoveryList' || !entry.data) continue;
            dispatch(
              discoveryApi.util.updateQueryData(
                'getDiscoveryList',
                entry.originalArgs as DiscoveryListRequest,
                (draft) => {
                  const results = draft.results as DiscoveryResultItem[];
                  const item = results?.find(
                    (r) => String(r.provider_id) === String(arg.object_id)
                  );
                  if (item) {
                    const newLink: DiscoveryResultCRMLink = {
                      id: result?.id as number | undefined,
                      crm: 'MEDRM',
                      crm_status: arg.fields?.status as string | undefined,
                    };
                    if (!item.crm_links?.length) {
                      item.crm_links = [newLink];
                    } else {
                      item.crm_links[0] = { ...item.crm_links[0], ...newLink };
                    }
                  }
                }
              )
            );
          }

          // MAP view still needs a refetch — it renders pins, not rows
          dispatch(
            discoveryApi.util.invalidateTags([
              { type: 'Discovery', id: 'MAP' },
            ])
          );

          // Invalidate provider cache for profile pages
          // Profile pages display CRM link status from provider.crm_links
          if (arg.object_id) {
            dispatch(
              providersApi.util.invalidateTags([
                { type: 'Provider', providerId: String(arg.object_id) },
              ] as any)
            );
          }

          // Invalidate Strategy Center cache so its provider list/map reflect
          // the newly pushed provider's CRM link status.
          dispatch(
            strategyApi.util.invalidateTags([...ALL_STRATEGY_MEMBER_TAGS])
          );
        } catch {
          // A rejected push is what records a failure, and invalidatesTags
          // only runs on success.
          dispatch(
            integrationsApi.util.invalidateTags([
              { type: CRM_OPERATION_FAILURES, id: 'LIST' },
            ])
          );
        }
      },
    }),

    linkToCRM: builder.mutation<void, LinkToCRMParams>({
      query: (data) => ({
        url: 'v1/integrations/link-to-crm-object/',
        method: 'POST',
        body: data,
      }),
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          await queryFulfilled;

          // Invalidate discovery cache to update CRM link status
          dispatch(
            discoveryApi.util.invalidateTags([
              { type: 'Discovery', id: 'LIST' },
              { type: 'Discovery', id: 'MAP' },
            ])
          );

          // Invalidate provider cache for profile pages
          // Profile pages display CRM link status from provider.crm_links
          if (arg.object_id) {
            dispatch(
              providersApi.util.invalidateTags([
                { type: 'Provider', providerId: String(arg.object_id) },
              ] as any)
            );
          }
        } catch {
          // Mutation failed, no need to invalidate
        }
      },
    }),

    linkToDuplicateCRMObject: builder.mutation<void, LinkToCRMParams>({
      query: (data) => ({
        url: 'v1/integrations/link-to-duplicate/',
        method: 'POST',
        body: data,
      }),
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          await queryFulfilled;

          // Invalidate discovery cache to update CRM link status
          dispatch(
            discoveryApi.util.invalidateTags([
              { type: 'Discovery', id: 'LIST' },
              { type: 'Discovery', id: 'MAP' },
            ])
          );

          // Invalidate provider cache for profile pages
          // Profile pages display CRM link status from provider.crm_links
          if (arg.object_id) {
            dispatch(
              providersApi.util.invalidateTags([
                { type: 'Provider', providerId: String(arg.object_id) },
              ] as any)
            );
          }
        } catch {
          // Mutation failed, no need to invalidate
        }
      },
    }),

    testPushToCRM: builder.mutation<Record<string, any>, TestPushToCRMParams>({
      query: ({ data, isDryRun }) => ({
        url: isDryRun
          ? 'v1/integrations/test-push-to-crm/?dry_run=TRUE'
          : 'v1/integrations/test-push-to-crm/',
        method: 'POST',
        body: data,
      }),
    }),

    startOneTimeSync: builder.mutation<OneTimeSyncTracker, StartOneTimeSyncParams>({
      query: ({ mappingId, direction }) => ({
        url: `v1/integrations/mappings/${mappingId}/one-time-sync-trackers/`,
        method: 'POST',
        body: {
          mapping: mappingId,
          direction,
        },
      }),
      invalidatesTags: [{ type: CRM_SYNC_STATUS, id: 'HISTORY' }],
    }),

    initiateIntegration: builder.mutation<void, InitiateIntegrationParams>({
      query: (data) => ({
        url: 'v1/integrations/initiate-integration/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [
        { type: INTEGRATION_TOKEN, id: 'TOKEN' },
        { type: AVAILABLE_INTEGRATIONS, id: 'LIST' },
      ],
    }),

    deleteCRMLink: builder.mutation<void, { id: number; object_id?: string }>({
      query: ({ id }) => ({
        url: `v1/integrations/crm-links/${id}/`,
        method: 'DELETE',
      }),
      async onQueryStarted({ object_id }, { queryFulfilled, dispatch }) {
        try {
          await queryFulfilled;

          // Invalidate discovery cache to update CRM link status
          dispatch(
            discoveryApi.util.invalidateTags([
              { type: 'Discovery', id: 'LIST' },
              { type: 'Discovery', id: 'MAP' },
            ])
          );

          // Invalidate provider cache for profile pages
          if (object_id) {
            dispatch(
              providersApi.util.invalidateTags([
                { type: 'Provider', providerId: String(object_id) },
              ] as any)
            );
          }

          // Invalidate Strategy Center cache so its provider list/map refetch
          // fresh crm_links. Without this the deleted link lingers in the
          // Strategy cache, the status reappears, and a repeat reset hits the
          // already-deleted link → 404 "No CRMLink matches the given query".
          dispatch(
            strategyApi.util.invalidateTags([...ALL_STRATEGY_MEMBER_TAGS])
          );
        } catch {
          // Mutation failed, no need to invalidate
        }
      },
    }),
  }),
});

export const {
  useGetMappingsQuery,
  useGetMappingByIdQuery,
  useSearchCRMQuery,
  useGetObjectTypeAttributesQuery,
  useGetCrmObjectsQuery,
  useGetSettingsQuery,
  useGetAutopopulateOptionsQuery,
  useGetUserInputOptionsQuery,
  useGetAvailableIntegrationsQuery,
  useGetIntegrationTokenQuery,
  useGetAllStatusesQuery,
  useGetCRMSyncHistoryQuery,
  useGetCRMOperationFailuresQuery,
  useAddMappingMutation,
  useUpdateMappingMutation,
  useDeleteMappingMutation,
  useAddMappingOptionMutation,
  useUpdateAutopopulateOptionMutation,
  useDeleteAutopopulateOptionMutation,
  useUpdateUserInputOptionMutation,
  useDeleteUserInputOptionMutation,
  useAddSettingMutation,
  useUpdateSettingMutation,
  useDeleteSettingMutation,
  usePushToCRMMutation,
  useLinkToCRMMutation,
  useLinkToDuplicateCRMObjectMutation,
  useTestPushToCRMMutation,
  useStartOneTimeSyncMutation,
  useInitiateIntegrationMutation,
  useDeleteCRMLinkMutation,
} = integrationsApi;
